import { Paths, PathValue } from "@mctypes/global.types";
import { Button, Flex, Paper, Radio, Stack, Title, Box } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../../components";
import {
  IGKFotometerResultater,
  IGKInneslutningsLekkasjetestMedFotometerResultater,
  IGKRapport,
} from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../../ReportBoard/ReportDocxImageContainer";
import { beregnetMaksimalLekkasjeverdi } from "../../gkGlobalUtils";
const InneslutningslekkasjetestMedFotometer: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKInneslutningsLekkasjetestMedFotometerResultater;
}> = ({ data, onUpdateEditData }) => {
  const addNewRow = () => {
    const newFilter: IGKFotometerResultater = {
      maalepunktNr: "",
      konstruksjonstype: "",
      maaleresultaterUrenSone: null,
      godkjent: null,
      maaleresultaterRenSone: null,
      maksimalLekkasjeverdi: null,
      beskrivelseAvLekkasjeLokasjon: "",
    };
    const updatedMaaleresultater: IGKFotometerResultater[] = [...data.inndata.maaleresultater, newFilter];
    onUpdateEditData(
      "testResultater.inneslutningsLekkasjeTestMedFotometer.inndata.maaleresultater",
      updatedMaaleresultater,
    );
    return updatedMaaleresultater;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = [...data.inndata.maaleresultater];
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData(
        "testResultater.inneslutningsLekkasjeTestMedFotometer.inndata.maaleresultater",
        updatedMaaleresultater,
      );
    }
  };
  const updateMaaleresultater = (resultat: string | number | boolean, i: number, parameter: string) => {
    const maaleresultater = [...data.inndata.maaleresultater];
    maaleresultater[i][parameter] = resultat;
    maaleresultater[i].maksimalLekkasjeverdi = beregnetMaksimalLekkasjeverdi(
      maaleresultater[i].maaleresultaterUrenSone,
    );
    onUpdateEditData("testResultater.inneslutningsLekkasjeTestMedFotometer.inndata.maaleresultater", maaleresultater);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          Inneslutningslekkasjetest med fotometer
        </Title>
        <Stack gap={0}>
          <b>Hvor mange målinger skal utføres? &#40;-&#41;:</b>
          <Flex>
            <Button color="green" onClick={addNewRow}>
              Legg til rad
            </Button>
            <HSpace />
            <Button variant="danger-filled" onClick={removeLastRow}>
              Fjern siste rad
            </Button>
          </Flex>
        </Stack>
        {Number(data.inndata.maaleresultater.length) > 0 && (
          <Flex direction="column" mt="lg">
            <b>Fyll inn måleresultater:</b>
            <table>
              <thead>
                <tr>
                  <th>Målepunktnummer &#40;-&#41;</th>
                  <th>Konstruksjonstype &#40;-&#41;</th>
                  <th>
                    Måleresultat uren sone &#40;mg/m<sup>3</sup>&#41;
                  </th>
                  <th style={{ display: "flex", justifyContent: "center" }}>Verdi høyere enn 0,01%?</th>
                  <th>Måleresultat ren sone &#40;%&#41;</th>
                  <th>Beskrivelse av lekkasjelokasjon &#40;-&#41;</th>
                </tr>
              </thead>
              {data.inndata.maaleresultater.map((filter, i) => (
                <tbody key={i}>
                  <tr>
                    <td>
                      <McInputNew
                        value={filter.maalepunktNr}
                        type="text"
                        onChange={(e) => updateMaaleresultater(e.target.value, i, "maalepunktNr")}
                        placeholder="Skriv inn målepunktnummer"
                      />
                    </td>
                    <td>
                      <McInputNew
                        value={filter.konstruksjonstype}
                        type="text"
                        onChange={(e) => updateMaaleresultater(e.target.value, i, "konstruksjonstype")}
                        placeholder="Skriv inn konstruksjonstype"
                      />
                    </td>
                    <td>
                      <McInputNew
                        value={filter.maaleresultaterUrenSone}
                        normalizeNumbers={true}
                        type="text"
                        onBlur={(e) => updateMaaleresultater(Number(e.target.value), i, "maaleresultaterUrenSone")}
                        placeholder="Måleresultat uren sone"
                      />
                    </td>
                    <td>
                      <Flex
                        direction="row"
                        justify="space-around"
                        align="center"
                        style={{ backgroundColor: "#ebecec", borderRadius: "6px", height: "2.2rem" }}
                      >
                        <Radio
                          checked={filter.godkjent === true}
                          onChange={() => updateMaaleresultater(true, i, "godkjent")}
                          label="Ja"
                        />
                        <Radio
                          checked={filter.godkjent === false}
                          onChange={() => updateMaaleresultater(false, i, "godkjent")}
                          label="Nei"
                        />
                      </Flex>
                    </td>
                    {filter.godkjent === true && (
                      <>
                        <td>
                          <McInputNew
                            value={filter.maaleresultaterRenSone}
                            normalizeNumbers={true}
                            type="text"
                            onBlur={(e) => updateMaaleresultater(Number(e.target.value), i, "maaleresultaterRenSone")}
                            placeholder="Måleresultat ren sone"
                          />
                        </td>
                        <td>
                          <McInputNew
                            value={filter.beskrivelseAvLekkasjeLokasjon}
                            type="text"
                            onChange={(e) => updateMaaleresultater(e.target.value, i, "beskrivelseAvLekkasjeLokasjon")}
                            placeholder="Beskrivelse av lekkasjelokasjon"
                          />
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              ))}
            </table>
          </Flex>
        )}
        {Number(data.inndata.maaleresultater.length) > 0 && (
          <Box mt="lg">
            <ReportDocxImageContainer docxImageTag="inneslutningsLekkasjeTestMedFotometer" title="Målepunkter" />
            <div style={{ width: "50%" }}>
              <McTextAreaNew
                label="Kommentar:"
                value={data.inndata.kommentar}
                onChange={(e) =>
                  onUpdateEditData(
                    "testResultater.inneslutningsLekkasjeTestMedFotometer.inndata.kommentar",
                    e.target.value,
                  )
                }
              />
            </div>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
export default InneslutningslekkasjetestMedFotometer;
